import {
  ConfigurationData,
  ElementDefinition,
  OperatorDefinition,
  CategoryDefinition,
} from '../types';

const API_BASE_URL = import.meta.env.REACT_APP_API_URL;

if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is not defined. Build cannot continue.');
}

// Cache configuration for 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

class ConfigurationService {
  private configCache: CacheEntry<ConfigurationData> | null = null;

  private getAuthToken(): string | null {
    return sessionStorage.getItem('access_token');
  }

  private async apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    const url = `${API_BASE_URL}/${cleanEndpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    headers['Authorization'] = `Bearer ${token}`;

    const config = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message ?? `API request failed with status ${response.status}`);
      }

      return (await response.json()) as T;
    } catch (error) {
      console.error(`Configuration API request failed: ${(error as Error).message}`);
      throw error;
    }
  }

  private isCacheValid<T>(cacheEntry: CacheEntry<T> | null | undefined): boolean {
    return (
      cacheEntry !== null &&
      cacheEntry !== undefined &&
      Date.now() - cacheEntry.timestamp < CACHE_DURATION
    );
  }

  private setCacheEntry<T>(data: T): CacheEntry<T> {
    return {
      data,
      timestamp: Date.now(),
    };
  }

  // Get all configuration data with caching
  async getConfiguration(): Promise<ConfigurationData> {
    if (this.isCacheValid(this.configCache)) {
      console.log('📋 Configuration: Using cached data');
      if (!this.configCache) throw new Error('Cache validation passed but cache is null');
      return this.configCache.data;
    }

    console.log('📋 Configuration: Cache miss, fetching from API');
    try {
      const data = await this.apiRequest<ConfigurationData>('/condition-elements');
      this.configCache = this.setCacheEntry(data);
      console.log('📋 Configuration: Successfully fetched and cached');
      return data;
    } catch (error) {
      console.error('Failed to load configuration from backend', error);

      // If we have cached data, use it even if expired
      if (this.configCache) {
        console.log('📋 Configuration: Using expired cached data as fallback');
        return this.configCache.data;
      }

      // No fallback - throw error to show user that backend is unavailable
      throw new Error(
        'Unable to connect to backend service. Please check your connection and try again.'
      );
    }
  }

  // Get specific element definition (uses client-side filtering instead of separate API call)
  async getElementDefinition(elementName: string): Promise<ElementDefinition | null> {
    try {
      const config = await this.getConfiguration();

      // Check regular elements first
      if (config.elements[elementName]) {
        return config.elements[elementName];
      }

      // Check custom signals
      if (config.custom_signals[elementName]) {
        return config.custom_signals[elementName];
      }

      return null;
    } catch (error) {
      console.error(`Failed to load element definition for ${elementName}`, error);
      return null;
    }
  }

  // Get elements by category (uses client-side filtering instead of separate API call)
  async getElementsByCategory(category: string): Promise<ElementDefinition[]> {
    try {
      const config = await this.getConfiguration();
      const elements: ElementDefinition[] = [];

      // Filter regular elements by category
      Object.values(config.elements).forEach(element => {
        if (element.category === category) {
          elements.push(element);
        }
      });

      // Filter custom signals by category
      Object.values(config.custom_signals).forEach(signal => {
        if (signal.category === category) {
          elements.push(signal);
        }
      });

      return elements;
    } catch (error) {
      console.error(`Failed to load elements for category ${category}`, error);
      return [];
    }
  }

  // Get available categories
  async getCategories(): Promise<Record<string, string | CategoryDefinition>> {
    const config = await this.getConfiguration();
    return config.categories;
  }

  // Get operator definitions
  async getOperators(): Promise<Record<string, OperatorDefinition>> {
    const config = await this.getConfiguration();
    return config.operators;
  }

  // Clear all caches
  clearCache(): void {
    this.configCache = null;
  }
}

// Export singleton instance
export const configurationService = new ConfigurationService();
export default configurationService;
